/**
 * @file marketReach.js
 * @description Market reach analytics service. Computes per-case therapist pool
 *   statistics including eligible, nearby, potential and strong match counts.
 *   Exports the haversine distance function and matchScore algorithm.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/marketReach
 */
// Market reach analytics — computes per-case therapist pool stats

import { loadTherapists, geocodeZip } from "./profileService";
import { evaluateApplicantFitScore, PROFILES, isMinimalTherapistProfile } from "./applicantFitEngine";

// ── Module-level caches ───────────────────────────────────────────────────────
// Therapist list: refreshed at most once every 10 minutes.
// Reach stats: per-case, refreshed at most once every 30 minutes.
// Both are in-memory only — cleared on page reload, which is acceptable because
// a fresh visit should reflect any case edits made in the interim.
const THERAPIST_TTL_MS = 10 * 60 * 1000; // 10 min
const REACH_TTL_MS     = 30 * 60 * 1000; // 30 min

let _therapistCache = null; // { data: EnrichedTherapist[], at: number } | null
const _reachCache   = new Map(); // caseId → { stats: object, at: number }

/**
 * Invalidates cached reach stats for a specific case (or all cases when caseId omitted).
 * Call this after a case is edited, posted, paused, closed, or archived so the widget
 * reflects the change on the next expand rather than serving 30-min-old data.
 */
export function bustReachCache(caseId) {
  if (caseId) _reachCache.delete(String(caseId));
  else        _reachCache.clear();
}

/** Internal: returns enriched therapist list from cache, refreshing when stale. */
async function getEnrichedTherapists() {
  const now = Date.now();
  if (_therapistCache && (now - _therapistCache.at) < THERAPIST_TTL_MS) {
    return _therapistCache.data;
  }
  const raw      = await loadTherapists();
  const enriched = await enrichTherapistCoords(raw);
  _therapistCache = { data: enriched, at: now };
  return enriched;
}

/**
 * @description Computes the great-circle distance in miles between two geographic coordinates.
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lng1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lng2 - Longitude of point 2
 * @returns {number} Distance in miles
 */
export function haversine(lat1, lng1, lat2, lng2) {
  const R = 3958.8; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * @description Scores how well a therapist matches a case (0–100). Delegates
 *   to the canonical applicantFitEngine under the MARKETPLACE profile, which
 *   reproduces the legacy weighting:
 *     - Role match (30)    — credential type vs case type
 *     - Skills overlap (30) — proportional via assessSkills
 *     - Hours compat (20)  — strict binary in-range via scoreDimensions.hours
 *     - Setting pref (20)  — skipped (excluded from denominator) when either
 *                            side has no settings; otherwise overlap-based
 *   Then the MARKETPLACE profile's postProcess applies:
 *     - +15 verification bonus when verificationStatus === "verified"
 *     - up to −20 distance penalty when actualDist > therapist.maxDistance
 *
 *   This is the same algorithm scorer B has always run, but now expressed
 *   declaratively in the engine. Calling this function is now a one-liner so
 *   the math can't drift between marketReach, the engine, and the UI.
 *
 * @param {Object} therapist - Therapist profile (credential, skills, settings,
 *   minHours, maxHours, verificationStatus, distanceMi, maxDistance, …)
 * @param {Object} caseObj - Case (type, skills, hours, settings|setting, …)
 * @returns {number} Match score 0–100 (0 when nothing scores).
 */
export function matchScore(therapist, caseObj) {
  return evaluateApplicantFitScore(therapist, caseObj, { profile: PROFILES.MARKETPLACE }) ?? 0;
}

/**
 * @description Default distance settings used by `computeCaseReach` when callers
 *   don't specify their own. Mirrors the defaults exposed by the AgencyListingsView
 *   settings modal so the precomputed reach stats and the live UI agree out of the box.
 */
export const DEFAULT_REACH_OPTS = {
  reachableCapMi:        40,
  defaultMaxDistanceMi:  20,
  // When true, skip the ZIP→lat/lng geocoding pass on therapists. Counts then
  // include ONLY therapists with real coordinates in the database, which is
  // the same set the map's loadTherapistsInRadius RPC returns. Use this when
  // you want the count to match what's visually rendered as a map pin.
  skipGeocodeEnrichment: false,
};

/**
 * @description Computes full reach statistics for a single case. Loads therapists
 *   if not provided, geocodes the case ZIP if lat/lng are missing.
 * @param {Object} caseObj - Case object with lat, lng, zip, type, skills, hours, etc.
 * @param {Object[]|null} [allTherapists] - Pre-loaded therapist list (reuse to avoid re-fetch)
 * @param {Object} [opts] - Distance configuration: { reachableCapMi, defaultMaxDistanceMi }.
 *                          Defaults to `DEFAULT_REACH_OPTS`.
 * @returns {Promise<{eligible: number, nearby: number, potentialMatches: number, strongMatches: number, avgMatchScore: number, verifiedMatches: number, verificationRate: number, funnel: Object}>}
 */
export async function computeCaseReach(caseObj, allTherapists = null, opts = {}) {
  const reachCap = opts.reachableCapMi        ?? DEFAULT_REACH_OPTS.reachableCapMi;
  const dflt     = opts.defaultMaxDistanceMi  ?? DEFAULT_REACH_OPTS.defaultMaxDistanceMi;
  const noEnrich = opts.skipGeocodeEnrichment ?? DEFAULT_REACH_OPTS.skipGeocodeEnrichment;

  // When called standalone (no pre-loaded list), use the cached therapist list.
  // This prevents every MarketReachWidget that opens before computeAllCasesReach
  // resolves from each firing an independent full-table fetch. With noEnrich,
  // skip the ZIP geocoding pass so we count only therapists with real
  // coordinates — matching what the map can render.
  const therapists = allTherapists
    || (noEnrich ? await loadTherapists() : await getEnrichedTherapists());

  // Resolve case lat/lng if missing
  let caseLat = parseFloat(caseObj.lat) || 0;
  let caseLng = parseFloat(caseObj.lng) || 0;
  if ((!caseLat || !caseLng) && caseObj.zip) {
    const coords = await geocodeZip(caseObj.zip);
    if (coords) { caseLat = coords.lat; caseLng = coords.lng; }
  }

  const eligible = therapists.filter(t => {
    const type = (t.type || "").toUpperCase();
    return type === caseObj.type || t.credential?.toUpperCase().startsWith(caseObj.type);
  });

  // Therapists with location data within range — reach distance is the
  // therapist's stated max travel, capped at `reachCap` (user-configurable).
  const nearby = (caseLat && caseLng)
    ? eligible.filter(t => {
        if (!t.lat || !t.lng) return false;
        const dist = haversine(caseLat, caseLng, t.lat, t.lng);
        const radius = Math.min(parseInt(t.maxDistance) || dflt, reachCap);
        return dist <= radius;
      })
    : eligible;

  // Candidate pool for match-quality metrics. For REMOTE cases, location is
  // irrelevant — the case recruits nationwide, so the pool is the full
  // type-eligible set, NOT the travel radius. (matchScore applies no distance
  // penalty here because therapist.distanceMi isn't set during reach scoring,
  // so scoring the wider pool is consistent with the radius scoring.) For
  // non-remote cases the pool stays the within-radius set.
  const isRemote = (caseObj.deliveryMode || caseObj.delivery_mode) === "remote";
  const pool = isRemote ? eligible : nearby;

  // Split the pool: minimal (role+ZIP) profiles are real DENSITY (they count in
  // `nearby`/`eligible`) but are NOT scoreable yet, so they're excluded from the
  // QUALITY metrics — otherwise a wave of fresh signups would tank avgMatchScore
  // and read as "nobody good" when it's really "supply arriving". `inProgress`
  // surfaces that incoming supply so the UI can frame it as momentum.
  const scoreablePool = pool.filter(t => !isMinimalTherapistProfile(t));
  const inProgress    = pool.length - scoreablePool.length;

  const scores = scoreablePool.map(t => matchScore(t, caseObj));
  const potentialMatches = scores.filter(s => s >= 40).length;
  const strongMatches    = scores.filter(s => s >= 70).length;
  const avgMatchScore    = scores.length > 0
    ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
    : 0;

  // David LOW-R7-12: surface verification breakdown so admin/agencies can
  // see how many potential matches are credentialed vs. unverified. Computed
  // over the scoreable pool (minimal profiles are unverified-by-definition and
  // would otherwise depress the rate without being real candidates yet).
  const verifiedInPool    = scoreablePool.filter(t => t.verificationStatus === "verified").length;
  const verifiedMatches   = scoreablePool.filter((t, i) => t.verificationStatus === "verified" && scores[i] >= 40).length;
  const verificationRate  = scoreablePool.length > 0 ? Math.round((verifiedInPool / scoreablePool.length) * 100) : 0;

  return {
    eligible:        eligible.length,
    // For remote, `nearby` carries the nationwide candidate count (the pool);
    // the UI relabels it "Candidates" when deliveryMode === remote.
    nearby:          pool.length,
    inProgress,      // minimal (role+ZIP) profiles in the pool — incoming supply, not yet scoreable
    poolScope:       isRemote ? "nationwide" : "radius",
    potentialMatches,
    strongMatches,
    avgMatchScore,
    verifiedMatches,
    verificationRate,
    funnel: {
      eligible:  eligible.length,
      nearby:    pool.length,
      potential: potentialMatches,
      strong:    strongMatches,
      viewed:    parseInt(caseObj.views)      || 0,
      applied:   parseInt(caseObj.applicants) || 0,
    },
  };
}

// Enrich therapists that have a zip but no lat/lng by batch-geocoding unique ZIPs.
// Results are cached in a module-level Map so subsequent calls are instant.
const _zipCoordCache = new Map();

async function enrichTherapistCoords(therapists) {
  // Collect unique ZIPs that need geocoding
  const needsGeocode = therapists.filter(t => (!t.lat || !t.lng) && t.zip);
  const uniqueZips = [...new Set(needsGeocode.map(t => t.zip))];

  // Geocode missing ZIPs (up to 8 at a time, 200ms between batches to respect Nominatim)
  for (let i = 0; i < uniqueZips.length; i += 8) {
    const batch = uniqueZips.slice(i, i + 8).filter(z => !_zipCoordCache.has(z));
    if (batch.length === 0) continue;
    await Promise.allSettled(
      batch.map(zip =>
        geocodeZip(zip).then(coords => {
          if (coords) _zipCoordCache.set(zip, coords);
        }).catch(() => {})
      )
    );
    if (i + 8 < uniqueZips.length) await new Promise(r => setTimeout(r, 200));
  }

  // Return enriched list — fill in coords from cache for those still missing them
  return therapists.map(t => {
    if (t.lat && t.lng) return t;
    const cached = t.zip ? _zipCoordCache.get(t.zip) : null;
    if (!cached) return t;
    return { ...t, lat: cached.lat, lng: cached.lng };
  });
}

/**
 * @description Batch-computes reach statistics for multiple cases. Loads therapists
 *   once, enriches them with geocoded coords, then iterates over all cases.
 * @param {Object[]} cases - Array of UI-shaped case objects
 * @param {Object} [opts] - Distance configuration: { reachableCapMi, defaultMaxDistanceMi }.
 *                          Cache entries are keyed by these values so different settings
 *                          coexist without invalidating each other.
 * @returns {Promise<Object>} Map of caseId → reach stats object (null on per-case error)
 */
export async function computeAllCasesReach(cases, opts = {}) {
  if (!cases || cases.length === 0) return {};

  const reachCap = opts.reachableCapMi        ?? DEFAULT_REACH_OPTS.reachableCapMi;
  const dflt     = opts.defaultMaxDistanceMi  ?? DEFAULT_REACH_OPTS.defaultMaxDistanceMi;
  const noEnrich = opts.skipGeocodeEnrichment ?? DEFAULT_REACH_OPTS.skipGeocodeEnrichment;
  // Cache key embeds the opts so changing settings doesn't serve stale data
  // computed against the old cap. Default-opts callers continue to share the
  // same cache slot they always have.
  const optsKey = `r${reachCap}:d${dflt}:e${noEnrich ? 0 : 1}`;
  const keyFor  = (caseId) => `${caseId}|${optsKey}`;

  const now = Date.now();

  // Short-circuit: if every case has a fresh cache entry for these opts, skip Supabase work.
  const allCached = cases.every(c => {
    const e = _reachCache.get(keyFor(c.id));
    return e && (now - e.at) < REACH_TTL_MS;
  });
  if (allCached) {
    return Object.fromEntries(cases.map(c => [c.id, _reachCache.get(keyFor(c.id)).stats]));
  }

  // Identify only the cases whose cache entry is missing or stale.
  const staleCases = cases.filter(c => {
    const e = _reachCache.get(keyFor(c.id));
    return !e || (now - e.at) >= REACH_TTL_MS;
  });

  // Load therapists once. With noEnrich, skip the ZIP geocoding pass so we
  // only count therapists with real DB coordinates — same set the map renders.
  const therapists = noEnrich ? await loadTherapists() : await getEnrichedTherapists();

  for (const c of staleCases) {
    try {
      const stats = await computeCaseReach(c, therapists, {
        reachableCapMi: reachCap,
        defaultMaxDistanceMi: dflt,
        skipGeocodeEnrichment: noEnrich,
      });
      _reachCache.set(keyFor(c.id), { stats, at: now });
    } catch (_) {
      // Don't cache failures — they'll retry on next call.
    }
  }

  // Return merged result: fresh cache for all cases (null on compute failure).
  return Object.fromEntries(
    cases.map(c => [c.id, _reachCache.get(keyFor(c.id))?.stats ?? null])
  );
}
